import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { avisoRegistrados, contarRegistrados } from '../lib/trabajadores-registrados';
import { getWorkerLimit } from '../lib/pricing';
import UpgradeLimitModal from '../components/UpgradeLimitModal';
import WorkerModal, { type Worker } from '../components/workers/WorkerModal';
import { useSedes } from '../lib/useSedes';
import { etiquetaSede, opcionesSede } from '../lib/sedes';
import { resumenPrioritaria } from '../lib/informes-puesto/ip-datos';
import { hoyEc } from '../lib/fechas';

// ── Types ──────────────────────────────────────────────────────────────────────

interface Warehouse { id: string; name: string; }
interface JobPosition { id: string; name: string; }

// workers_count = declarado en el Perfil SSO; workers_registered_count = registrados (M39-bis, lo mantiene el trigger).
interface SSOProfile { workers_count: number | null; workers_registered_count: number | null; paritario_type: string | null; }

// ── Helpers ────────────────────────────────────────────────────────────────────

function isExamDue(lastExam: string | null): boolean {
  if (!lastExam) return true;
  const due = new Date(lastExam);
  due.setFullYear(due.getFullYear() + 1);
  const threshold = new Date();
  threshold.setDate(threshold.getDate() + 30);
  return due <= threshold;
}

const RISK_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  bajo:  { bg: 'rgba(34,197,94,0.1)',  color: '#22c55e', border: 'rgba(34,197,94,0.3)'  },
  medio: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  alto:  { bg: 'rgba(239,68,68,0.1)',  color: '#ef4444', border: 'rgba(239,68,68,0.3)'  },
};

const APTITUDE_COLORS: Record<string, string> = {
  apto:                   '#22c55e',
  apto_con_restricciones: '#f59e0b',
  no_apto:                '#ef4444',
  pendiente:              '#64748b',
};

const APTITUDE_LABELS: Record<string, string> = {
  apto:                   'Apto',
  apto_con_restricciones: 'Con restricciones',
  no_apto:                'No apto',
  pendiente:              'Pendiente',
};

const STATUS_COLORS: Record<string, string> = {
  activo:       '#22c55e',
  licencia:     '#f59e0b',
  desvinculado: '#475569',
};

const STATUS_LABELS: Record<string, string> = {
  activo:       'Activo',
  licencia:     'En licencia',
  desvinculado: 'Desvinculado',
};

const CONTRACT_LABELS: Record<string, string> = {
  indefinido:  'Indefinido',
  plazo_fijo:  'Plazo fijo',
  obra_cierta: 'Obra cierta',
  pasantia:    'Pasantía',
  otro:        'Otro',
};

// ── CSV Export ─────────────────────────────────────────────────────────────────

function exportCSV(workers: Worker[], warehouses: Warehouse[]) {
  const wMap = new Map(warehouses.map(w => [w.id, w.name]));
  const header = ['Nombre', 'Cédula', 'Cargo', 'Bodega/Centro', 'Contrato', 'Estado', 'Riesgo', 'Aptitud médica', 'Teletrabajador', 'Último examen'];
  const rows = workers.map(w => [
    w.full_name,
    w.cedula ?? '',
    w.position,
    w.warehouse_id ? (wMap.get(w.warehouse_id) ?? '') : '',
    w.contract_type ? CONTRACT_LABELS[w.contract_type] ?? w.contract_type : '',
    STATUS_LABELS[w.status] ?? w.status,
    w.risk_level ?? '',
    w.medical_aptitude ? APTITUDE_LABELS[w.medical_aptitude] ?? w.medical_aptitude : '',
    w.is_teleworker ? 'Sí' : 'No',
    w.last_medical_exam ?? '',
  ]);
  const csv = [header, ...rows].map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `trabajadores-${hoyEc()}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function WorkersPage({ onNavigateToSSOPerfil }: { onNavigateToSSOPerfil?: () => void } = {}) {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany = impersonated ?? company;
  const companyId = displayCompany?.id ?? '';

  const canEdit = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;

  const [workers, setWorkers]         = useState<Worker[]>([]);
  const [warehouses, setWarehouses]   = useState<Warehouse[]>([]);
  const [jobPositions, setJobPositions] = useState<JobPosition[]>([]);
  const [ssoProfile, setSsoProfile]   = useState<SSOProfile | null>(null);
  const [loading, setLoading]       = useState(true);

  // Filters
  const [search, setSearch]         = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterRisk, setFilterRisk] = useState('');
  // F1a: sede (filtro, columna y campo del formulario) solo con 2 o más sedes activas.
  const { sedes, varias: conSede } = useSedes(companyId);
  const [filterSede, setFilterSede] = useState('');
  const sedeMap = new Map(sedes.map(s => [s.id, s]));

  // Modal
  const [modalWorker, setModalWorker] = useState<Worker | null | undefined>(undefined); // undefined = closed
  const openCreate = () => setModalWorker(null);
  const openEdit   = (w: Worker) => setModalWorker(w);
  const closeModal = () => setModalWorker(undefined);

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const maxWorkers = getWorkerLimit(displayCompany?.plan);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    const [{ data: ws }, { data: whs }, { data: sso }, { data: jps }] = await Promise.all([
      supabase.from('workers').select('*').eq('company_id', companyId).order('full_name'),
      supabase.from('warehouses').select('id, name').eq('company_id', companyId).order('name'),
      supabase.from('company_sso_profile').select('workers_count, workers_registered_count, paritario_type').eq('company_id', companyId).maybeSingle(),
      supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name'),
    ]);
    setWorkers((ws ?? []) as Worker[]);
    setWarehouses((whs ?? []) as Warehouse[]);
    setJobPositions((jps ?? []) as JobPosition[]);
    setSsoProfile(sso ?? null);
    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);
  // Lote D (TH 1): solo el conteo, del servidor, para el administrador y el responsable (sin nombres ni tipo).
  const [priorityCount, setPriorityCount] = useState<number | null>(null);
  useEffect(() => { if (canEdit) resumenPrioritaria().then(r => setPriorityCount(r ? r.con_condicion : null)); }, [canEdit, workers]);

  function handleSaved() { closeModal(); loadData(); }

  // ── Metrics ──────────────────────────────────────────────────────────────────

  const activeWorkers   = workers.filter(w => w.status === 'activo');
  const atWorkerLimit   = maxWorkers !== null && activeWorkers.length >= maxWorkers;
  const teleworkCount   = activeWorkers.filter(w => w.is_teleworker).length;
  const examsDue        = activeWorkers.filter(w => isExamDue(w.last_medical_exam)).length;

  // PE-A4d: registrados (en memoria; mismo criterio que workers_registered_count) frente a declarados (Perfil SSO).
  // Reemplaza la alerta de "organismo paritario cambiado", que comparaba el paritario de los registrados con el del
  // declarado: con 3 registrados de 158 declarados decía que el comité ya no hacía falta, y lo que manda es el declarado.
  const registrados = contarRegistrados(workers);
  const aviso = ssoProfile && !loading ? avisoRegistrados(registrados, ssoProfile.workers_count) : null;

  // ── Filtered table ───────────────────────────────────────────────────────────

  const filtered = workers.filter(w => {
    if (filterStatus && w.status !== filterStatus) return false;
    if (filterWarehouse && w.warehouse_id !== filterWarehouse) return false;
    if (filterRisk && w.risk_level !== filterRisk) return false;
    if (conSede && filterSede && w.site_id !== filterSede) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!w.full_name.toLowerCase().includes(q) && !(w.cedula ?? '').includes(q)) return false;
    }
    return true;
  });

  const warehouseMap = new Map(warehouses.map(w => [w.id, w.name]));

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Trabajadores</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {activeWorkers.length} trabajador{activeWorkers.length !== 1 ? 'es' : ''} activo{activeWorkers.length !== 1 ? 's' : ''}
            {maxWorkers !== null && (
              <span style={{ color: atWorkerLimit ? '#ef4444' : '#475569', fontWeight: 600 }}>
                {' '}· {activeWorkers.length}/{maxWorkers}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => exportCSV(filtered, warehouses)} style={secondaryBtn}>
            Exportar CSV
          </button>
          {canEdit && (
            <button
              onClick={() => { if (atWorkerLimit) setShowUpgradeModal(true); else openCreate(); }}
              style={primaryBtn}
              title={atWorkerLimit ? `Límite del plan alcanzado (${maxWorkers} trabajadores)` : undefined}
            >
              + Agregar trabajador
            </button>
          )}
        </div>
      </div>

      {/* Plan limit banner */}
      {atWorkerLimit && canEdit && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px',
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 10, color: '#f87171', fontSize: '0.84rem',
        }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🔒</span>
          <div>
            <strong>Límite del plan alcanzado.</strong>{' '}
            Has alcanzado el máximo de {maxWorkers} trabajadores. Actualiza tu plan para agregar más.
          </div>
        </div>
      )}

      {/* Registrados frente a declarados (informativo, no bloquea nada) */}
      {aviso && (
        <div role="status" aria-label="Trabajadores registrados y declarados" style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderRadius: 10, fontSize: '0.84rem',
          ...(aviso.tipo === 'info'
            ? { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: '#7dd3fc' }
            : { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24' }),
        }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }} aria-hidden>{aviso.tipo === 'info' ? 'ℹ️' : '⚠️'}</span>
          <div>
            {aviso.texto}
            {aviso.sugerencia && (
              <>
                {' '}{aviso.sugerencia}{' '}
                {onNavigateToSSOPerfil && (
                  <button type="button" onClick={onNavigateToSSOPerfil} style={linkBtn}>Ir al Perfil SSO</button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        <MetricCard label="Total activos" value={activeWorkers.length} color="#2FBF71" bg="rgba(47,191,113,0.1)" />
        {priorityCount !== null && <MetricCard label="Con atención prioritaria" value={priorityCount} color="#a78bfa" bg="rgba(167,139,250,0.1)" />}
        <MetricCard label="Teletrabajadores" value={teleworkCount} color="#38bdf8" bg="rgba(56,189,248,0.1)" />
        <MetricCard
          label="Exámenes vencidos / por vencer"
          value={examsDue}
          color={examsDue > 0 ? '#ef4444' : '#22c55e'}
          bg={examsDue > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)'}
          badge={examsDue > 0}
        />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...filterInput, flex: '1 1 200px', minWidth: 160 }}
          placeholder="Buscar por nombre o cédula…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={filterInput} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Estado: todos</option>
          <option value="activo">Activo</option>
          <option value="licencia">En licencia</option>
          <option value="desvinculado">Desvinculado</option>
        </select>
        {conSede && (
          <select style={filterInput} value={filterSede} onChange={e => setFilterSede(e.target.value)} aria-label="Filtrar por sede">
            <option value="">Sede: todas</option>
            {opcionesSede(sedes).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <div style={{ flex: '0 1 220px', minWidth: 160 }}>
          <SelectBuscable inputStyle={{ ...filterInput, paddingRight: 32 }} ariaLabel="Filtrar por bodega" value={filterWarehouse}
            onChange={setFilterWarehouse} vacio="Bodega: todas" opciones={warehouses.map(w => ({ value: w.id, label: w.name }))} />
        </div>
        <select style={filterInput} value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
          <option value="">Riesgo: todos</option>
          <option value="bajo">Bajo</option>
          <option value="medio">Medio</option>
          <option value="alto">Alto</option>
        </select>
        {(search || filterStatus || filterWarehouse || filterRisk || (conSede && filterSede)) && (
          <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterWarehouse(''); setFilterRisk(''); setFilterSede(''); }}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.82rem' }}>
            ✕ Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {workers.length === 0 ? 'Aún no hay trabajadores registrados.' : 'Sin resultados para los filtros aplicados.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  {['Nombre', 'Cédula', 'Cargo', ...(conSede ? ['Sede'] : []), 'Bodega/Centro', 'Contrato', 'Riesgo', 'Aptitud médica', 'Estado', ''].map(h => (
                    <th key={h} style={{
                      padding: '11px 14px', textAlign: 'left', color: '#64748b',
                      fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.04em',
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(w => (
                  <tr
                    key={w.id}
                    onClick={() => canEdit && openEdit(w)}
                    style={{
                      borderBottom: '1px solid rgba(51,65,85,0.5)',
                      cursor: canEdit ? 'pointer' : 'default',
                      opacity: w.status === 'desvinculado' ? 0.55 : 1,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: '#e2e8f0' }}>{w.full_name}</div>
                      {w.is_teleworker && <span style={{ fontSize: '0.68rem', color: '#38bdf8' }}>Teletrabajo</span>}
                    </td>
                    <td style={{ ...td, color: '#94a3b8' }}>{w.cedula ?? '—'}</td>
                    <td style={{ ...td, color: '#cbd5e1' }}>{w.position}</td>
                    {conSede && (
                      <td style={{ ...td, color: '#94a3b8' }}>
                        {w.site_id && sedeMap.get(w.site_id) ? etiquetaSede(sedeMap.get(w.site_id)!) : '—'}
                      </td>
                    )}
                    <td style={{ ...td, color: '#94a3b8' }}>
                      {w.warehouse_id ? (warehouseMap.get(w.warehouse_id) ?? '—') : '—'}
                    </td>
                    <td style={{ ...td, color: '#94a3b8' }}>
                      {w.contract_type ? CONTRACT_LABELS[w.contract_type] ?? w.contract_type : '—'}
                    </td>
                    <td style={td}>
                      {w.risk_level ? (
                        <Badge
                          label={w.risk_level.charAt(0).toUpperCase() + w.risk_level.slice(1)}
                          colors={RISK_COLORS[w.risk_level]}
                        />
                      ) : <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td style={td}>
                      {w.medical_aptitude ? (
                        <span style={{ color: APTITUDE_COLORS[w.medical_aptitude], fontWeight: 600, fontSize: '0.8rem' }}>
                          {APTITUDE_LABELS[w.medical_aptitude]}
                        </span>
                      ) : <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td style={td}>
                      <span style={{
                        color: STATUS_COLORS[w.status],
                        fontWeight: 600, fontSize: '0.8rem',
                      }}>
                        {STATUS_LABELS[w.status]}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {canEdit && (
                        <button
                          onClick={e => { e.stopPropagation(); openEdit(w); }}
                          style={{
                            padding: '5px 12px', background: 'transparent', border: '1px solid #334155',
                            color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#2FBF71'; e.currentTarget.style.color = '#2FBF71'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Conteo */}
      {filtered.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filtered.length} de {workers.length} trabajador{workers.length !== 1 ? 'es' : ''}
        </p>
      )}

      {/* Modal */}
      {modalWorker !== undefined && (
        <WorkerModal
          companyId={companyId}
          warehouses={warehouses}
          jobPositions={jobPositions}
          sedes={sedes}
          worker={modalWorker}
          onSaved={handleSaved}
          onClose={closeModal}
        />
      )}

      {showUpgradeModal && (
        <UpgradeLimitModal
          resource="workers"
          currentUsage={activeWorkers.length}
          plan={displayCompany?.plan}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color, bg, badge }: {
  label: string; value: number; color: string; bg: string; badge?: boolean;
}) {
  return (
    <div style={{
      background: '#1e293b', border: '1px solid #334155', borderRadius: 12,
      padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{value}</span>
        {badge && value > 0 && (
          <span style={{
            background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 20, padding: '2px 8px', fontSize: '0.68rem', fontWeight: 700,
          }}>
            Acción
          </span>
        )}
      </div>
      <div style={{
        height: 3, borderRadius: 2, background: bg,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: color, opacity: 0.5 }} />
      </div>
      <span style={{ color: '#64748b', fontSize: '0.76rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

function Badge({ label, colors }: { label: string; colors: { bg: string; color: string; border: string } }) {
  return (
    <span style={{
      background: colors.bg, color: colors.color, border: `1px solid ${colors.border}`,
      borderRadius: 6, padding: '2px 8px', fontSize: '0.74rem', fontWeight: 700,
    }}>
      {label}
    </span>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const primaryBtn: React.CSSProperties = {
  padding: '9px 20px', background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
  border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem',
  borderRadius: 8, cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  padding: '9px 16px', background: 'transparent', border: '1px solid #334155',
  color: '#94a3b8', fontWeight: 600, fontSize: '0.84rem', borderRadius: 8, cursor: 'pointer',
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, fontSize: 'inherit',
  textDecoration: 'underline', cursor: 'pointer',
};

const filterInput: React.CSSProperties = {
  padding: '8px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155',
  borderRadius: 8, color: '#e2e8f0', fontSize: '0.84rem', outline: 'none',
};

const td: React.CSSProperties = {
  padding: '11px 14px', verticalAlign: 'middle',
};
